import { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { Link } from 'expo-router';
import {
  ActivityIndicator,
  Animated,
  Easing,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';

import logoSource from '../../assets/images/mcqgen-logo-trimmed.png';

type ScreenProps = PropsWithChildren<{
  eyebrow?: string;
  footer?: ReactNode;
  subtitle: string;
  title: string;
}>;

type ButtonProps = {
  disabled?: boolean;
  loading?: boolean;
  onPress?: () => void;
  title: string;
  variant?: 'primary' | 'secondary';
};

type InputProps = React.ComponentProps<typeof TextInput> & {
  helper?: string;
  label: string;
};

export function AuthScreen({ children, eyebrow, footer, subtitle, title }: ScreenProps) {
  const insets = useSafeAreaInsets();
  const heroAnim = useRef(new Animated.Value(0)).current;
  const panelAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.stagger(120, [
      Animated.timing(heroAnim, {
        toValue: 1,
        duration: 520,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(panelAnim, {
        toValue: 1,
        duration: 560,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [heroAnim, panelAnim]);

  return (
    <SafeAreaView edges={['top', 'bottom']} style={styles.safeArea}>
      <LinearGradient
        colors={['#103427', '#163828', '#0D281D']}
        locations={[0, 0.58, 1]}
        style={StyleSheet.absoluteFill}
      />
      <View style={[styles.glow, styles.glowTop]} />
      <View style={[styles.glow, styles.glowMiddle]} />
      <View style={[styles.glow, styles.glowBottom]} />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          {
            paddingTop: Math.max(insets.top, 10) + 12,
            paddingBottom: Math.max(insets.bottom, 20) + 16,
          },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <Animated.View
          style={[
            styles.hero,
            {
              opacity: heroAnim,
              transform: [
                {
                  translateY: heroAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [18, 0],
                  }),
                },
              ],
            },
          ]}>
          <View style={styles.brandRow}>
            <View style={styles.brandMark}>
              <Image contentFit="contain" source={logoSource} style={styles.logo} />
            </View>
            <View style={styles.brandCopy}>
              <Text style={styles.brandKicker}>MCQGen</Text>
              <Text style={styles.brandSub}>School assessment workspace</Text>
            </View>
          </View>

          {eyebrow ? <Text style={styles.eyebrow}>{eyebrow}</Text> : null}
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subtitle}>{subtitle}</Text>
        </Animated.View>

        <Animated.View
          style={[
            styles.panel,
            {
              opacity: panelAnim,
              transform: [
                {
                  translateY: panelAnim.interpolate({
                    inputRange: [0, 1],
                    outputRange: [24, 0],
                  }),
                },
              ],
            },
          ]}>
          {children}
        </Animated.View>

        {footer ? <View style={styles.footer}>{footer}</View> : null}
      </ScrollView>
    </SafeAreaView>
  );
}

export function AuthInput({ helper, label, style, ...props }: InputProps) {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <TextInput
        placeholderTextColor="#90A196"
        style={[styles.input, style]}
        {...props}
      />
      {helper ? <Text style={styles.helper}>{helper}</Text> : null}
    </View>
  );
}

export function AuthButton({
  disabled = false,
  loading = false,
  onPress,
  title,
  variant = 'primary',
}: ButtonProps) {
  const buttonStyle = variant === 'primary' ? styles.primaryButton : styles.secondaryButton;
  const textStyle =
    variant === 'primary' ? styles.primaryButtonText : styles.secondaryButtonText;

  return (
    <Pressable
      disabled={disabled || loading}
      onPress={onPress}
      style={({ pressed }) => [
        styles.buttonBase,
        buttonStyle,
        (disabled || loading) && styles.buttonDisabled,
        pressed && !(disabled || loading) && styles.buttonPressed,
      ]}>
      {loading ? (
        <ActivityIndicator color={variant === 'primary' ? '#FFFFFF' : '#163828'} />
      ) : (
        <Text style={textStyle}>{title}</Text>
      )}
    </Pressable>
  );
}

export function AuthNote({ children, tone = 'neutral' }: PropsWithChildren<{ tone?: 'neutral' | 'warn' }>) {
  return (
    <View style={[styles.note, tone === 'warn' && styles.warnNote]}>
      <Text style={[styles.noteText, tone === 'warn' && styles.warnNoteText]}>{children}</Text>
    </View>
  );
}

export function AuthLinkRow({
  actionHref,
  actionLabel,
  prompt,
}: {
  actionHref: '/forgot-password' | '/sign-in' | '/sign-up';
  actionLabel: string;
  prompt: string;
}) {
  return (
    <View style={styles.linkRow}>
      <Text style={styles.linkPrompt}>{prompt} </Text>
      <Link href={actionHref} style={styles.linkAction}>
        {actionLabel}
      </Link>
    </View>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#103427',
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 18,
    gap: 16,
  },
  glow: {
    position: 'absolute',
    borderRadius: 999,
    opacity: 0.28,
  },
  glowTop: {
    width: 280,
    height: 280,
    top: -110,
    right: -110,
    backgroundColor: '#2D6C48',
  },
  glowMiddle: {
    width: 160,
    height: 160,
    top: 180,
    left: -60,
    backgroundColor: '#7BA883',
    opacity: 0.14,
  },
  glowBottom: {
    width: 320,
    height: 320,
    bottom: -150,
    left: -120,
    backgroundColor: '#E7D8B5',
    opacity: 0.12,
  },
  hero: {
    gap: 10,
    paddingTop: 8,
    paddingHorizontal: 4,
  },
  brandRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  brandMark: {
    width: 72,
    height: 72,
    borderRadius: 22,
    backgroundColor: 'rgba(255,255,255,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  logo: {
    width: 54,
    height: 54,
  },
  brandCopy: {
    gap: 2,
  },
  brandKicker: {
    color: '#F7F4EE',
    fontSize: 18,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: -0.2,
  },
  brandSub: {
    color: '#B9C8BE',
    fontSize: 12,
    fontWeight: '600',
  },
  eyebrow: {
    color: '#BBD0C2',
    fontSize: 11,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
  },
  title: {
    color: '#F7F4EE',
    fontSize: 32,
    lineHeight: 37,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: -0.8,
  },
  subtitle: {
    maxWidth: 340,
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    fontFamily: Fonts.serif,
  },
  panel: {
    borderRadius: 30,
    backgroundColor: '#F6F1E8',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.28)',
    padding: 20,
    gap: 18,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.16,
    shadowRadius: 28,
    elevation: 8,
  },
  field: {
    gap: 7,
  },
  fieldLabel: {
    color: '#163828',
    fontSize: 12,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
    letterSpacing: 0.8,
  },
  input: {
    minHeight: 58,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: '#D7E1D8',
    backgroundColor: '#FBF8F2',
    paddingHorizontal: 16,
    color: '#12261D',
    fontSize: 15,
  },
  helper: {
    color: '#6A7D6F',
    fontSize: 12,
    lineHeight: 17,
  },
  buttonBase: {
    minHeight: 56,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: '#163828',
    shadowColor: '#163828',
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.24,
    shadowRadius: 20,
    elevation: 8,
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: '#D7E1D8',
    backgroundColor: '#FBF8F2',
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  secondaryButtonText: {
    color: '#163828',
    fontSize: 15,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  buttonDisabled: {
    opacity: 0.55,
  },
  buttonPressed: {
    transform: [{ scale: 0.99 }],
  },
  note: {
    borderRadius: 18,
    backgroundColor: '#EEF5EE',
    borderWidth: 1,
    borderColor: '#DCE8DE',
    padding: 14,
  },
  warnNote: {
    backgroundColor: '#FFF7E8',
    borderWidth: 1,
    borderColor: '#F0D6A0',
  },
  noteText: {
    color: '#536556',
    fontSize: 13,
    lineHeight: 20,
  },
  warnNoteText: {
    color: '#8D6512',
  },
  footer: {
    gap: 12,
    alignItems: 'center',
    paddingTop: 2,
  },
  linkRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  linkPrompt: {
    color: '#6D8071',
    fontSize: 13,
    fontWeight: '600',
  },
  linkAction: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    textDecorationLine: 'underline',
  },
});
